import type { TreeNode } from 'echarts/types/src/data/Tree';
import type {
  RectProps,
  TextProps,
  LineProps,
  CircleProps,
  PolygonProps,
  PathState
} from 'zrender';
import { eachAfter, eachBefore } from 'echarts/lib/chart/tree/traversalHelper';
import {
  init,
  firstWalk,
  secondWalk,
  separation as sep,
  getViewRect
} from 'echarts/lib/chart/tree/layoutHelper';
import type {
  TreeLayoutNode,
  separation as separationType
} from 'echarts/types/src/chart/tree/layoutHelper';
import type { LayoutRect } from 'echarts/types/src/util/layout';
import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import type { ShapeProps as PolylineRadiusShapeProps } from './shape';
import type {
  TwoWayTreeSeriesModelClazz as TwoWayTreeSeriesModel,
  TwoWayTreeSeriesNodeItemOption,
  RootNodeRenderOption,
  NormalNodeRenderOption,
  GroupNodeRenderOption
} from './TwoWayTreeSeries';
import { measureText, defaultsDeep, judgingTheSignOfNumber } from './util';

export const STATE = {
  /** 节点选中状态 */
  SELECTED: 'selected',
  /** 选中链路（祖先高亮） */
  SELECTED_LINK: 'selected_link'
} as const;

export const NODE_TYPE = {
  /** 根节点 */
  ROOT: 'root',
  /** 普通节点 */
  NORMAL: 'normal',
  /** 分组节点 */
  GROUP: 'group'
} as const;

/** 基准字体尺寸 */
const BASE_FONT_SIZE = 10;
/** 根节点 */
const ROOT_NODE_WIDTH = BASE_FONT_SIZE * 15.8;
const ROOT_NODE_HEIGHT = BASE_FONT_SIZE * 7.2;
/** 普通节点 */
const NORMAL_NODE_WIDTH = BASE_FONT_SIZE * 12;
const NORMAL_NODE_HEIGHT = BASE_FONT_SIZE * 5;
/** 相同父节点的间距 */
const SAME_PARENT_ROW_SPACE = BASE_FONT_SIZE * 0.8;
/** 不同父节点的间距 */
const DIFF_PARENT_ROW_SPACE = BASE_FONT_SIZE * 1.6;
/** 树层级之间间距 */
const COLUMN_SPACE = BASE_FONT_SIZE * 1.6;
/** 层级控制 */
const NODE_ITEM_Z = 10;
const NODE_ITEM_CHILD_Z = 20;
const NODE_LINE_Z = 0;

export type LayoutInfo = {
  x: number;
  y: number;
  width?: number;
  space?: number;
  hasSibling?: boolean;
  isEdge?: boolean;
  noRender?: boolean;
  renderOption: TwoWayTreeSeriesNodeItemOption['render'];
};

export function layoutRootNode(node: TreeNode, seriesModel: TwoWayTreeSeriesModel) {
  const nodeData = node.hostTree.data.getRawDataItem(
    node.dataIndex
  ) as unknown as TwoWayTreeSeriesNodeItemOption;
  const nodeLayoutInfo = node.getLayout() as LayoutInfo;
  const renderOption: RootNodeRenderOption = {
    item: {
      x: nodeLayoutInfo.x,
      y: nodeLayoutInfo.y,
      children: {}
    }
  };

  // * item - rect

  const rect = (renderOption.item.children.rect || {}) as RectProps;

  renderOption.item.children.rect = rect;

  rect.type = 'rect';
  rect.shape = {
    r: BASE_FONT_SIZE * 0.5,
    width: ROOT_NODE_WIDTH,
    height: ROOT_NODE_HEIGHT
  };
  rect.shape.x = -rect.shape.width / 2;
  rect.shape.y = -rect.shape.height / 2;
  rect.x = 0;
  rect.y = 0;
  rect.z = NODE_ITEM_Z;

  rect.states = {
    [STATE.SELECTED]: {
      style: {
        stroke: '#3366FF',
        lineWidth: BASE_FONT_SIZE * 0.2,
        shadowBlur: BASE_FONT_SIZE * 0.6,
        shadowColor: 'rgba(0,0,0,0.16)'
      }
    },
    [STATE.SELECTED_LINK]: {
      style: {
        stroke: '#3366FF'
      }
    }
  } as Record<string, PathState>;

  // * item - text
  if (nodeData.render.item.children.text) {
    const text = (renderOption.item.children.text || {}) as TextProps;
    renderOption.item.children.text = text;

    text.type = 'text';
    text.z = NODE_ITEM_CHILD_Z;
  }

  // * item - attachment
  if (nodeData.render.item.children.attachment) {
    const attachment = (renderOption.item.children.attachment || {}) as TextProps;
    renderOption.item.children.attachment = attachment;

    attachment.type = 'text';
    attachment.style = {
      y: BASE_FONT_SIZE * 0.2
    };
    attachment.z = NODE_ITEM_CHILD_Z;
  }

  // merge
  defaultsDeep(
    defaultsDeep(renderOption, seriesModel.option?.render?.root || {}, {
      overlay: true,
      depth: Infinity
    }),
    nodeData.render,
    { overlay: true, depth: Infinity }
  );

  // ! 超出 8 个字时变为省略号

  if ((renderOption.item.children.text.style.text || '').length > 8) {
    renderOption.item.children.text.style.text = (
      renderOption.item.children.text.style.text || ''
    ).replace(/(.{0,7}).*/, '$1...');
  }

  // ! 根据正负值判断颜色
  renderOption.item.children.attachment.style.fill =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    renderOption.item.children.attachment.style._fill[
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      judgingTheSignOfNumber(renderOption.item.children.attachment.style.text) + 1
    ];

  node.setLayout({ renderOption }, true);

  //
}

// eslint-disable-next-line sonarjs/no-identical-functions
export function layoutNormalNode(node: TreeNode, seriesModel: TwoWayTreeSeriesModel) {
  const nodeData = node.hostTree.data.getRawDataItem(
    node.dataIndex
  ) as unknown as TwoWayTreeSeriesNodeItemOption;
  const nodeLayoutInfo = node.getLayout() as LayoutInfo;
  const isLeftTree =
    nodeLayoutInfo.x < (node.hostTree.root.children[0].getLayout() as LayoutInfo).x ? -1 : 1;
  const renderOption: NormalNodeRenderOption = {
    item: {
      x: nodeLayoutInfo.x,
      y: nodeLayoutInfo.y,
      children: {}
    },
    line: {
      children: {}
    }
  };

  // * item - rect
  const rect = (renderOption.item.children.rect || {}) as RectProps;
  renderOption.item.children.rect = rect;

  rect.type = 'rect';
  rect.shape = {
    r: BASE_FONT_SIZE * 0.5,
    width: NORMAL_NODE_WIDTH,
    height: NORMAL_NODE_HEIGHT
  };
  rect.shape.x = -rect.shape.width / 2;
  rect.shape.y = -rect.shape.height / 2;
  rect.x = 0;
  rect.y = 0;
  rect.z = NODE_ITEM_Z + 1;

  rect.states = {
    [STATE.SELECTED]: {
      style: {
        stroke: '#3366FF',
        lineWidth: BASE_FONT_SIZE * 0.2,
        shadowBlur: BASE_FONT_SIZE * 0.6,
        shadowColor: 'rgba(0,0,0,0.16)'
      }
    },
    [STATE.SELECTED_LINK]: {
      style: {
        stroke: '#3366FF'
      }
    }
  } as Record<string, PathState>;

  // * item - text
  if (nodeData.render.item.children.text) {
    const text = (renderOption.item.children.text || {}) as TextProps;
    renderOption.item.children.text = text;

    text.type = 'text';
    text.z = NODE_ITEM_CHILD_Z;
  }

  // * item - attachment
  if (nodeData.render.item.children.attachment) {
    const attachment = (renderOption.item.children.attachment || {}) as TextProps;
    renderOption.item.children.attachment = attachment;

    attachment.type = 'text';
    attachment.style = {
      y: BASE_FONT_SIZE * 0.2
    };
    attachment.z = NODE_ITEM_CHILD_Z;
  }

  // * line
  const parentNodeLayoutInfo = node.parentNode.getLayout() as LayoutInfo;
  const line = (renderOption.line.children.line || {}) as PolygonProps;
  renderOption.line.children.line = line;

  line.type = 'dvPolylineRadius';
  line.shape = {
    r: [0, nodeLayoutInfo.isEdge ? BASE_FONT_SIZE * 0.4 : 0],
    points: [
      [parentNodeLayoutInfo.x, parentNodeLayoutInfo.y],
      [
        nodeLayoutInfo.x + (nodeLayoutInfo.width / 2 + nodeLayoutInfo.space / 2) * isLeftTree * -1,
        parentNodeLayoutInfo.y
      ],
      [
        nodeLayoutInfo.x + (nodeLayoutInfo.width / 2 + nodeLayoutInfo.space / 2) * isLeftTree * -1,
        nodeLayoutInfo.y
      ],
      [nodeLayoutInfo.x, nodeLayoutInfo.y]
    ]
  } as PolylineRadiusShapeProps;
  line.z = NODE_LINE_Z;

  line.states = {
    [STATE.SELECTED_LINK]: {
      style: {
        stroke: '#3366FF'
      },
      z: NODE_LINE_Z + 1
    }
  } as Record<string, PathState>;

  // merge
  defaultsDeep(
    defaultsDeep(renderOption, seriesModel.option?.render?.normal || {}, {
      overlay: true,
      depth: Infinity
    }),
    nodeData.render,
    { overlay: true, depth: Infinity }
  );

  // ! 超出 8 个字时变为省略号

  if ((renderOption.item.children.text.style.text || '').length > 8) {
    renderOption.item.children.text.style.text = (
      renderOption.item.children.text.style.text || ''
    ).replace(/(.{0,7}).*/, '$1...');
  }

  // ! 根据正负值判断颜色

  renderOption.item.children.attachment.style.fill =
    renderOption.item.children.attachment.style._fill[
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      judgingTheSignOfNumber(renderOption.item.children.attachment.style.text) + 1
    ];

  node.setLayout({ renderOption }, true);

  //
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
function layoutGroupNodeItem(
  orient: 'LR' | 'RL',
  node: TreeNode,
  seriesModel: TwoWayTreeSeriesModel
) {
  const nodeData = node.hostTree.data.getRawDataItem(
    node.dataIndex
  ) as unknown as TwoWayTreeSeriesNodeItemOption;
  const isLeftTree = orient === 'RL' ? -1 : 1;
  const renderOption: GroupNodeRenderOption = {
    item: {
      children: {}
    }
  };

  // * item - circle
  const circle = (renderOption.item.children.circle || {}) as CircleProps;
  renderOption.item.children.circle = circle;

  circle.type = 'circle';
  circle.shape = {
    r: (BASE_FONT_SIZE * 1.45) / 2
  };
  circle.style = {
    fill: '#3366FF'
  };
  circle.x = circle.shape.r * isLeftTree;
  // circle.x = 0;
  circle.z = NODE_ITEM_Z;

  // * item - circleLine
  const circleLine = (renderOption.item.children.circleLine || {}) as LineProps;
  renderOption.item.children.circleLine = circleLine;

  circleLine.type = 'line';
  circleLine.shape = {
    x1: circle.shape.r * 0.5 * isLeftTree,
    y1: 0,
    x2: circle.shape.r * 1.5 * isLeftTree,
    y2: 0
  };
  circleLine.style = {
    stroke: '#fff',
    lineWidth: BASE_FONT_SIZE * 0.1,
    opacity: node.isExpand ? 1 : 0
  };
  circleLine.silent = true;
  circleLine.z = NODE_ITEM_CHILD_Z;

  // * item - circleText
  const circleText = (renderOption.item.children.circleText || {}) as TextProps;
  renderOption.item.children.circleText = circleText;

  circleText.type = 'text';
  circleText.style = {
    fontFamily: 'THSJinRongTi-Medium',
    fontSize: BASE_FONT_SIZE * 1,
    fill: '#fff',
    align: 'center',
    verticalAlign: 'middle',
    text: String(node.children.length),
    opacity: node.isExpand ? 0 : 1
  };
  circleText.x = circle.shape.r * isLeftTree;
  circleText.z = NODE_ITEM_CHILD_Z;
  circleText.silent = true;

  // * item - labelText
  let labelText: TextProps;
  if (nodeData.render.item.children.labelText) {
    labelText = (renderOption.item.children.labelText || {}) as TextProps;
    renderOption.item.children.labelText = labelText;

    labelText.type = 'text';
    labelText.z = NODE_ITEM_CHILD_Z;
    labelText.silent = true;

    labelText.states = {
      [STATE.SELECTED_LINK]: {
        style: {
          fill: '#3366FF'
        }
      }
    } as Record<string, PathState>;

    // merge
    defaultsDeep(
      defaultsDeep(labelText, seriesModel.option?.render?.group?.item?.children?.labelText || {}, {
        overlay: true,
        depth: Infinity
      }),

      nodeData.render?.item?.children?.labelText,
      { overlay: true, depth: Infinity }
    );
  }

  // * item - labelRect
  let labelRect: RectProps;
  if (labelText) {
    const labelTextMeasure = measureText({
      fontFamily: labelText.style.fontFamily,
      fontSize: labelText.style.fontSize,
      text: labelText.style.text
    });

    labelRect = (renderOption.item.children.labelRect || {}) as RectProps;
    renderOption.item.children.labelRect = labelRect;

    labelRect.type = 'rect';
    labelRect.shape = {
      r: BASE_FONT_SIZE * 0.2,
      width: BASE_FONT_SIZE * 1 + labelTextMeasure.width,
      height: Number(labelText.style.fontSize) * 1.5
    };
    labelRect.shape.y = -labelRect.shape.height / 2;
    labelRect.x =
      isLeftTree === -1
        ? (circle.shape.r * 2 + labelRect.shape.width + BASE_FONT_SIZE * 0.8) * -1
        : circle.shape.r * 2 + BASE_FONT_SIZE * 0.8;
    labelRect.z = NODE_ITEM_Z;

    labelRect.states = {
      [STATE.SELECTED_LINK]: {
        style: {
          stroke: '#3366FF'
        }
      }
    } as Record<string, PathState>;

    labelText.x = labelRect.x + labelRect.shape.width / 2;
  }

  // * item - Interactive hotspot
  if (labelText) {
    const interactive = (renderOption.item.children.interactive || {}) as RectProps;
    renderOption.item.children.interactive = interactive;

    interactive.type = 'rect';
    interactive.shape = {
      width: isLeftTree === -1 ? labelRect.x * -1 : labelRect.x + labelRect.shape.width,
      height: labelRect.shape.height
    };
    interactive.shape.y = -labelRect.shape.height / 2;
    interactive.style = {
      fill: 'transparent'
    };
    interactive.z = NODE_ITEM_Z;
  }

  // merge

  renderOption.item.children = defaultsDeep(
    defaultsDeep(
      renderOption.item.children,

      seriesModel.option?.render?.group?.item.children || {},
      {
        overlay: true,
        depth: Infinity
      }
    ),

    nodeData.render.item.children,
    { overlay: true, depth: Infinity }
  );

  node.setLayout({ renderOption }, true);

  let width = 0;
  if (labelText) {
    if (isLeftTree === -1) {
      width = labelRect.x * -1;
    } else {
      width = labelRect.x + labelRect.shape.width;
    }
  } else {
    width = circle.shape.r * 2;
  }

  return { width };
}

// eslint-disable-next-line complexity, max-lines-per-function, sonarjs/cognitive-complexity
export function layoutGroupNode(node: TreeNode, seriesModel: TwoWayTreeSeriesModel) {
  const nodeData = node.hostTree.data.getRawDataItem(
    node.dataIndex
  ) as unknown as TwoWayTreeSeriesNodeItemOption;
  const nodeLayoutInfo = node.getLayout() as LayoutInfo;
  const isLeftTree =
    nodeLayoutInfo.x < (node.hostTree.root.children[0].getLayout() as LayoutInfo).x ? -1 : 1;
  const renderOption: GroupNodeRenderOption = defaultsDeep(
    {
      item: {
        x: nodeLayoutInfo.x,
        y: nodeLayoutInfo.y
      },
      line: {
        children: {}
      }
    },
    nodeLayoutInfo.renderOption,
    { overlay: true, depth: Infinity }
  );

  // * line
  const parentNodeLayoutInfo = node.parentNode.getLayout() as LayoutInfo;

  const line = (renderOption.line.children.line || {}) as PolygonProps;
  renderOption.line.children.line = line;

  line.type = 'dvPolylineRadius';
  line.shape = {
    r: [0, nodeLayoutInfo.isEdge ? BASE_FONT_SIZE * 0.4 : 0],
    points: [
      [parentNodeLayoutInfo.x, parentNodeLayoutInfo.y],
      [nodeLayoutInfo.x + (nodeLayoutInfo.space / 2) * -1 * isLeftTree, parentNodeLayoutInfo.y],
      [nodeLayoutInfo.x + (nodeLayoutInfo.space / 2) * -1 * isLeftTree, nodeLayoutInfo.y],
      [nodeLayoutInfo.x + (renderOption.item?.children?.labelRect?.x ?? 0), nodeLayoutInfo.y]
    ]
  } as PolylineRadiusShapeProps;
  line.z = NODE_LINE_Z;

  line.states = {
    [STATE.SELECTED_LINK]: {
      style: {
        stroke: '#3366FF'
      },
      z: NODE_LINE_Z + 1
    }
  } as Record<string, PathState>;

  // * line - linePlaceholder
  // if (line) {
  //
  //   render.line.children.linePlaceholder = {
  //     type: 'line',
  //     shape: {
  //       x1: layoutInfo.x,
  //       y1: layoutInfo.y,
  //       x2:
  //         layoutInfo.x +
  //         (labelText
  //           ? labelRect.x + (labelRect.shape.width / 2) * isLeftTree
  //           : circle.x + circle.shape.r * isLeftTree),
  //       y2: layoutInfo.y
  //     },
  //     style: {
  //       ...line.style
  //     },
  //     z: NODE_LINE_Z
  //   } as LineProps;
  // }

  // merge
  defaultsDeep(
    defaultsDeep(renderOption, seriesModel.option?.render?.group || {}, {
      overlay: true,
      depth: Infinity
    }),
    nodeData.render,
    { overlay: true, depth: Infinity }
  );

  node.setLayout({ renderOption }, true);

  //
}

type Box = {
  left: number;
  right: number;
  width: number;
  height: number;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function layoutTree(
  orient: 'LR' | 'RL',
  virtualRoot: TreeLayoutNode,
  realRoot: TreeLayoutNode,
  separation: ReturnType<typeof separationType>,
  box: Box,
  seriesModel: TwoWayTreeSeriesModel
) {
  if (realRoot) {
    init(virtualRoot);

    eachAfter(realRoot, firstWalk, separation);

    virtualRoot.hierNode.modifier = -realRoot.hierNode.prelim;

    eachBefore(realRoot, secondWalk);

    let left = realRoot;
    let right = realRoot;
    let bottom = realRoot;

    const widthArray: number[] = [0];
    const spaceArray: number[] = [0];

    eachBefore(realRoot, (node: TreeLayoutNode) => {
      const { x } = node.getLayout() as LayoutInfo;
      if (x < (left.getLayout() as LayoutInfo).x) {
        left = node;
      }
      if (x > (right.getLayout() as LayoutInfo).x) {
        right = node;
      }
      if (node.depth > bottom.depth) {
        bottom = node;
      }

      // -----------------------------------
      const item = node.hostTree.data.getRawDataItem(
        node.dataIndex
      ) as TwoWayTreeSeriesNodeItemOption;

      spaceArray[node.depth] = spaceArray[node.depth] || 0;
      widthArray[node.depth] = widthArray[node.depth] || 0;

      if (node.depth === 1 || item.type === NODE_TYPE.ROOT) {
        spaceArray[node.depth] = 0;
        widthArray[node.depth] = ROOT_NODE_WIDTH / 2;
      } else if (node.depth % 2 === 0 || item.type === NODE_TYPE.GROUP) {
        spaceArray[node.depth] = Math.max(
          spaceArray[node.depth],
          node.parentNode.children.length > 1 ? COLUMN_SPACE * 2 : COLUMN_SPACE
        );
        widthArray[node.depth] = Math.max(
          widthArray[node.depth],
          layoutGroupNodeItem(orient, node, seriesModel).width
        );
      } else if (node.depth % 2 === 1 || item.type === NODE_TYPE.NORMAL) {
        spaceArray[node.depth] = Math.max(
          spaceArray[node.depth],
          node.parentNode.children.length > 1 ? COLUMN_SPACE * 2 : COLUMN_SPACE
        );
        widthArray[node.depth] = NORMAL_NODE_WIDTH;
      }

      const sibling = node.parentNode.children
        .slice()
        .sort((a, b) => (a.getLayout() as LayoutInfo).x - (b.getLayout() as LayoutInfo).x);

      sibling[0].setLayout({ isEdge: true }, true);
      sibling[sibling.length - 1].setLayout({ isEdge: true }, true);
    });

    const delta = left === right ? 1 : separation(left, right) / 2;
    const tx = delta - (left.getLayout() as LayoutInfo).x;
    let kx = 0;
    let ky = 0;
    let coorX = 0;
    let coorY = 0;

    if (orient === 'RL' || orient === 'LR') {
      // ky = box.height / (right.getLayout().x + delta + tx);
      ky = NORMAL_NODE_HEIGHT;
      // kx = box.width / (bottom.depth - 1 || 1);

      const offsetArray = widthArray.slice();
      offsetArray.forEach((item, index) => {
        const all = widthArray[index] + spaceArray[index];
        if (index > 0) {
          offsetArray[index] = all + offsetArray[index - 1];
        }
      });

      eachBefore(realRoot, (node: TreeLayoutNode) => {
        const hasSibling = node.parentNode.children.length > 1;
        const item = node.hostTree.data.getRawDataItem(
          node.dataIndex
        ) as TwoWayTreeSeriesNodeItemOption;

        // const offset = (node.depth - 1) * kx;
        let offset = offsetArray[node.depth - 1];
        if (node.depth === 1 || item.type === NODE_TYPE.ROOT) {
          //
        } else if (node.depth % 2 === 0 || item.type === NODE_TYPE.GROUP) {
          offset += spaceArray[node.depth];
        } else if (node.depth % 2 === 1 || item.type === NODE_TYPE.NORMAL) {
          offset += spaceArray[node.depth] + widthArray[node.depth] / 2;
        }

        coorY = ((node.getLayout() as LayoutInfo).x + tx) * ky;
        coorX = orient === 'LR' ? box.left + offset : box.right - offset;

        node.setLayout(
          {
            x: coorX,
            y: coorY,
            width: widthArray[node.depth],
            space: spaceArray[node.depth],
            hasSibling
          },
          true
        );
      });
    } else if (orient === 'TB' || orient === 'BT') {
      kx = box.width / ((right.getLayout() as LayoutInfo).x + delta + tx);
      ky = box.height / (bottom.depth - 1 || 1);

      eachBefore(realRoot, (node: TreeLayoutNode) => {
        coorX = ((node.getLayout() as LayoutInfo).x + tx) * kx;
        coorY = orient === 'TB' ? (node.depth - 1) * ky : box.height - (node.depth - 1) * ky;

        node.setLayout({ x: coorX, y: coorY }, true);
      });

      //
    }

    //
  }
}

// see src/chart/tree/treeLayout.ts
// eslint-disable-next-line sonarjs/cognitive-complexity
function commonLayout(seriesModel: TwoWayTreeSeriesModel, api: ExtensionAPI) {
  const layoutInfo = getViewRect(seriesModel, api) as LayoutRect;
  seriesModel.layoutInfo = layoutInfo;

  let width = 0;
  let height = 0;
  // const sepBuild = sep();
  // * 让间距相同（不区分是否同一个父节点）
  // const sepBuild = (() => 1) as unknown as ReturnType<typeof separationType>;
  /**
   * 叶子节点的间距
   *
   * 叶子高度 50，同父节点时，间距为 8，不同父节点时间距为 16
   */
  const sepBuild = (node1: TreeLayoutNode, node2: TreeLayoutNode) => {
    return node1.parentNode === node2.parentNode
      ? 1 + SAME_PARENT_ROW_SPACE / NORMAL_NODE_HEIGHT
      : 1 + DIFF_PARENT_ROW_SPACE / NORMAL_NODE_HEIGHT;
  };

  width = layoutInfo.width;
  height = layoutInfo.height;

  // ! clear old layout info
  seriesModel.leftTree.clearLayouts();
  seriesModel.rightTree.clearLayouts();

  const leftTreeVirtualRoot = seriesModel.leftTree.root as TreeLayoutNode;
  const leftTreeRealRoot = leftTreeVirtualRoot.children[0];

  layoutTree(
    'RL',
    leftTreeVirtualRoot,
    leftTreeRealRoot,
    sepBuild,
    {
      left: 0,
      right: width / 2,
      width: width / 2,
      height
    },
    seriesModel
  );

  const rightTreeVirtualRoot = seriesModel.rightTree.root as TreeLayoutNode;
  const rightTreeRealRoot = rightTreeVirtualRoot.children[0];

  layoutTree(
    'LR',
    rightTreeVirtualRoot,
    rightTreeRealRoot,
    sepBuild,
    {
      left: width / 2,
      right: width,
      width: width / 2,
      height
    },
    seriesModel
  );

  // ! 将左右子树的 根节点 y 坐标对齐
  const diffY = rightTreeRealRoot.getLayout().y - leftTreeRealRoot.getLayout().y;

  eachBefore(rightTreeRealRoot, (node: TreeLayoutNode) => {
    node.setLayout({ x: node.getLayout().x, y: node.getLayout().y - diffY }, true);
  });

  // ! 将其中一颗子树的根节点标记一下，避免重复绘制
  rightTreeRealRoot.setLayout({ noRender: true }, true);
}

export default function twoWayTreeLayout(ecModel: GlobalModel, api: ExtensionAPI) {
  ecModel.eachSeriesByType('dvTwoWayTree', (seriesModel: TwoWayTreeSeriesModel) => {
    commonLayout(seriesModel, api);
  });
}
